/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { combineReducers } from 'redux';
import { SagaIterator } from 'redux-saga';
import { all, call, put, delay, select, takeLeading, race, take, takeLatest } from 'redux-saga/effects';
import {
  ActionRequest,
  SubscriptionEntitlement,
  DestructuredDuration,
  DurationUtils,
  Ducks,
  ActionSuccess,
} from '@wit/vtv-x-foundation-lib';
import * as Ppv from './ppv';
import * as Subscription from './subscription';
import * as Collection from './collection';
import * as Assets from './assets';
import * as EntitledAssets from './assets/entitledAssets';
import * as SubscriptionFolders from './assets/subscriptionsFolders';

enum ActionTypes {
  SCHEDULE_CACHING_REFRESH = 'entitlements/SCHEDULE_CACHING_REFRESH',
  CACHING_REFRESH = 'entitlements/CACHING_REFRESH',
}

type Request = ActionRequest<ActionTypes.SCHEDULE_CACHING_REFRESH>;
type Refresh = ActionSuccess<ActionTypes.CACHING_REFRESH>;

type Actions = Request | Refresh;

const Actions = {
  scheduleCachingRefresh: () => ({ type: ActionTypes.SCHEDULE_CACHING_REFRESH }),
  refresh: () => ({ type: ActionTypes.CACHING_REFRESH }),
};

export const ActionCreators = {
  scheduleCachingRefresh: () => Actions.scheduleCachingRefresh(),
  refresh: () => Actions.refresh(),
};

// combine all the consumption reducers
export default combineReducers({
  ppv: Ppv.default,
  subscription: Subscription.default,
  collection: Collection.default,
  assets: Assets.default,
});

function* requestSubscriptions(): SagaIterator {
  yield put(Subscription.ActionCreators.request());
  yield race([take(Subscription.ActionTypes.SUCCESS), take(Subscription.ActionTypes.FAIL)]);

  const subscriptionEntitlements: SubscriptionEntitlement[] | undefined = yield select(
    Subscription.Selectors.getSubscriptionEntitlements
  );

  if (!subscriptionEntitlements?.length) {
    return;
  }

  const subscriptionIds: string[] = subscriptionEntitlements.map((e) => e.id);

  yield put(SubscriptionFolders.ActionCreators.getSubscriptionsFolders(subscriptionIds));
}

function* requestAllEntitlements(): SagaIterator {
  yield put(Ppv.ActionCreators.request());
  yield put(Collection.ActionCreators.request());

  yield all([
    race([take(Ppv.ActionTypes.SUCCESS), take(Ppv.ActionTypes.FAIL)]),
    race([take(Collection.ActionTypes.SUCCESS), take(Collection.ActionTypes.FAIL)]),
  ]);

  yield put(EntitledAssets.ActionCreators.getEntitlementsAssets());

  yield call(requestSubscriptions);
}

function* scheduleEntitlementsCachingRefresh(): SagaIterator {
  const cachingRefresh: DestructuredDuration | undefined = yield select(Ducks.Dms.Selectors.getCachingRefresh);

  while (cachingRefresh) {
    yield delay(DurationUtils.destructuredDurationtoMilliseconds(cachingRefresh));
    yield call(requestAllEntitlements);
  }
}

function* resetEntitlements(): SagaIterator {
  yield Ducks.Pagination.clearPaginatedApi([
    Ppv.PAGINATED_ENTITLEMENTS_NAMESPACE,
    Subscription.PAGINATED_ENTITLEMENTS_NAMESPACE,
    Collection.PAGINATED_ENTITLEMENTS_NAMESPACE,
    EntitledAssets.PAGINATED_ENTITLEMENTS_COLLECTION_NAMESPACE,
    EntitledAssets.PAGINATED_ENTITLEMENTS_PPV_NAMESPACE,
    SubscriptionFolders.PAGINATED_ENTITLEMENTS_NAMESPACE,
  ]);
}

function* refreshAllEntitlements(): SagaIterator {
  yield call(resetEntitlements);
  yield call(requestAllEntitlements);
}

function* saga(): SagaIterator {
  yield all([
    takeLeading(ActionTypes.SCHEDULE_CACHING_REFRESH, scheduleEntitlementsCachingRefresh),
    takeLeading(ActionTypes.CACHING_REFRESH, refreshAllEntitlements),
    takeLatest(Ducks.Credentials.ActionTypes.LOGOUT_SUCCESS, resetEntitlements),
  ]);
}

export const sagas = [call(Ppv.saga), call(Subscription.saga), call(Collection.saga), ...Assets.sagas, call(saga)];
